import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const typographyVariants = cva('font-sans', {
  variants: {
    variant: {
      h1: 'text-4xl font-bold leading-tight',
      h2: 'text-3xl font-semibold leading-tight',
      h3: 'text-2xl font-semibold leading-tight',
      h4: 'text-xl font-semibold leading-tight',
      h5: 'text-lg font-medium leading-tight',
      h6: 'text-base font-medium leading-tight',
      p: 'text-base leading-relaxed',
      lead: 'text-lg leading-relaxed',
      large: 'text-lg leading-relaxed',
      small: 'text-sm leading-relaxed',
      subtle: 'text-xs leading-relaxed',
      blockquote: 'text-lg italic border-l-4 pl-4',
      code: 'text-sm font-mono bg-muted px-2 py-1 rounded',
      muted: 'text-sm text-muted-foreground',
    },
    size: {
      '10': 'text-[10px]',
      '12': 'text-xs',
      '14': 'text-sm',
      '16': 'text-base',
      '18': 'text-lg',
      '20': 'text-2xl',
      '22': 'text-xl',
      '24': 'text-2xl',
      '34': 'text-3xl',
      '36': 'text-4xl',
    },
    weight: {
      thin: 'font-thin',
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extrabold: 'font-extrabold',
    },
    color: {
      primary: 'text-primary',
      secondary: 'text-secondary-foreground',
      destructive: 'text-destructive',
      success: 'text-green-600',
      alert: 'text-orange-600',
      muted: 'text-muted-foreground',
      neutral50: 'text-neutral-50',
      neutral100: 'text-neutral-100',
      neutral200: 'text-neutral-200',
      neutral300: 'text-neutral-300',
      neutral400: 'text-neutral-400',
      neutral500: 'text-neutral-500',
      neutral600: 'text-neutral-600',
      neutral700: 'text-neutral-700',
      neutral800: 'text-neutral-800',
      neutral900: 'text-neutral-900',
      neutral1000: 'text-neutral-1000',
      white: 'text-white',
      black: 'text-black',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    },
  },
  defaultVariants: {
    variant: 'p',
    size: '16',
    weight: 'normal',
    color: 'neutral900',
    align: 'left',
  },
});

type TypographyVariantProps = VariantProps<typeof typographyVariants>;

export interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    Omit<TypographyVariantProps, 'color'> {
  as?: keyof React.JSX.IntrinsicElements;
  color?: TypographyVariantProps['color'];
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    { className, variant, size, weight, color, align, as, style, ...props },
    ref,
  ) => {
    const Component = as || 'div';
    return React.createElement(Component, {
      className: cn(
        typographyVariants({ variant, size, weight, color, align, className }),
      ),
      style: { fontFamily: 'Inter, system-ui, sans-serif', ...style },
      ref,
      ...props,
    });
  },
);

Typography.displayName = 'Typography';

// Heading components
export const H1 = React.forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h1" as="h1" {...props} />);
H1.displayName = 'H1';

export const H2 = React.forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h2" as="h2" {...props} />);
H2.displayName = 'H2';

export const H3 = React.forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h3" as="h3" {...props} />);
H3.displayName = 'H3';

export const H4 = React.forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h4" as="h4" {...props} />);
H4.displayName = 'H4';

export const H5 = React.forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h5" as="h5" {...props} />);
H5.displayName = 'H5';

export const H6 = React.forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h6" as="h6" {...props} />);
H6.displayName = 'H6';

// Text components
export const P = React.forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="p" as="p" {...props} />);
P.displayName = 'P';

export const Blockquote = React.forwardRef<
  HTMLQuoteElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => (
  <Typography ref={ref} variant="blockquote" as="blockquote" {...props} />
));
Blockquote.displayName = 'Blockquote';

export const Code = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="code" as="code" {...props} />);
Code.displayName = 'Code';

export const Lead = React.forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="lead" as="p" {...props} />);
Lead.displayName = 'Lead';

export const Large = React.forwardRef<
  HTMLDivElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="large" as="div" {...props} />);
Large.displayName = 'Large';

export const Small = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => (
  <Typography ref={ref} variant="small" as="small" {...props} />
));
Small.displayName = 'Small';

export const Muted = React.forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="muted" as="p" {...props} />);
Muted.displayName = 'Muted';

export const Subtle = React.forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="subtle" as="p" {...props} />);
Subtle.displayName = 'Subtle';

export { typographyVariants };
export default Typography;
